/**
 * Hook for managing analytics graph state and data transformations
 *
 * Extracted from useAnalyticsData.js to reduce complexity.
 * Handles graph state, KPI datasets, and derived graph info.
 */
import { useState, useMemo, useCallback } from 'react';

import {
  GRAPH_COLORS,
  getCategoricalColor,
} from '../util/analytics/colorUtils';
import {
  createDefaultGraphStates,
} from '../util/analytics/constants';
import {
  sumArray,
  getNumberSegmentsMany,
} from '../util/analytics/dataTransformers';

/**
 * Custom hook for analytics graph state management
 *
 * @returns {Object} Graph state and handlers
 */
export default function useAnalyticsGraphs() {
  // KPI colors
  const [KPIColors, setKPIColors] = useState([]);

  // Graph state
  const [allGraphStates, setAllGraphStates] = useState(createDefaultGraphStates);

  // KPI dataset details for modal
  const [sortedKPIDatasetDetails, setSortedKPIDatasetDetails] = useState([]);
  const [partialKPIDatasetDetails, setPartialKPIDatasetDetails] = useState([]);

  /**
   * Update KPI colors when validKPIs change
   */
  const updateKPIColors = useCallback((kpiCount) => {
    // Fixed palette assigned by KPI index — stable across loads, unlike the
    // previous random colors, and checkable for colorblind safety.
    setKPIColors(Array.from({ length: kpiCount }, (_, i) => getCategoricalColor(i)));
  }, []);

  /**
   * Set graph states from time series data
   * Note: Uses refs to access current validKPIs/KPIColors to keep callback stable
   */
  const setGraphStates = useCallback((dateLabels, timeSeries, kpis, colors) => {
    // Use passed kpis/colors or fall back to current state
    const currentKPIs = kpis || [];
    const currentColors = colors || [];

    const KPIDatasets = [];

    currentKPIs.forEach((KPI, i) => {
      if (KPI in timeSeries) {
        KPIDatasets.push({
          data: timeSeries[KPI],
          color: () => currentColors[i],
        });
      }
    });

    // Create sorted KPI dataset details
    const KPIDatasetDetails = KPIDatasets.map((dataset, index) => ({
      dataset,
      label: currentKPIs[index],
      sum: sumArray(dataset['data']),
    }));
    const sorted = KPIDatasetDetails.slice().sort((a, b) => b.sum - a.sum);
    setSortedKPIDatasetDetails(sorted);
    setPartialKPIDatasetDetails({ labels: dateLabels, datasets: sorted.slice(0, 5) });

    // V3: Use consistent colors that match legend
    setAllGraphStates({
      AF: {
        labels: dateLabels,
        datasets: [
          { data: timeSeries['PF'], color: () => GRAPH_COLORS.GRAY },
          { data: timeSeries['RS'], color: () => GRAPH_COLORS.AMBER },
          { data: timeSeries['SO'], color: () => GRAPH_COLORS.BLUE },
        ],
      },
      RD: {
        labels: dateLabels,
        datasets: [
          { data: timeSeries['CR'], color: () => GRAPH_COLORS.PURPLE },
          { data: timeSeries['DE'], color: () => GRAPH_COLORS.RED },
        ],
      },
      AR: {
        labels: dateLabels,
        datasets: [
          { data: timeSeries['ALL'], color: () => GRAPH_COLORS.BLUE },
        ],
      },
      KPI: {
        labels: dateLabels,
        datasets: KPIDatasets,
      },
    });
  }, []);

  /**
   * Reset graph states to defaults (derived info is computed via useMemo)
   */
  const resetGraphStates = useCallback(() => {
    setAllGraphStates(createDefaultGraphStates);
    setSortedKPIDatasetDetails([]);
    setPartialKPIDatasetDetails([]);
  }, []);

  // Calculate derived graph info when graph states change (memoized)
  const allDerivedGraphInfo = useMemo(() => {
    const output = {};
    Object.keys(allGraphStates).forEach((key) => {
      output[key] = {
        segments: getNumberSegmentsMany(allGraphStates[key]?.datasets || []),
      };
    });
    return output;
  }, [allGraphStates]);

  return {
    // KPI colors
    KPIColors,
    updateKPIColors,

    // Graph state
    allGraphStates,
    allDerivedGraphInfo,

    // KPI dataset details
    sortedKPIDatasetDetails,
    partialKPIDatasetDetails,

    // Actions
    setGraphStates,
    resetGraphStates,
  };
}
